import React, { useCallback, useEffect, useState } from "react";
import Loading from "../../Common/Loading";
import CustomAlert from "../../Common/CustomAlert";

interface RoleGroup {
  type: "header" | "subheader" | "role";
  label: string;
  id?: string;
  checked?: boolean;
}

interface Column {
  col: number;
  groups: RoleGroup[];
}

interface ApiGetResponse {
  columns: Column[];
  reset_md: boolean;
  count: number;
  error: string;
  status_msg: string;
}

interface AlertState {
  visible: boolean;
  message: string;
  type: "E" | "A" | "I";
}

const API_BASE_URL =
  process.env.REACT_APP_DOCUMENT_LINK;

async function apiFetchRoles(s: string, f: string, l: string): Promise<ApiGetResponse> {
  const res = await fetch(
    `${API_BASE_URL}api/tutor-admin-roles/?S=${encodeURIComponent(s)}&F=${encodeURIComponent(f)}&L=${encodeURIComponent(l)}`
  );
  if (!res.ok) throw new Error("Failed to load roles");
  return res.json();
}

async function apiSaveRoles(body: {
  school_code: string; family_code: string; login: string;
  permissions: string; reset_md: boolean;
}): Promise<ApiGetResponse> {
  const res = await fetch(`${API_BASE_URL}api/tutor-admin-roles/save/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to save roles");
  return res.json();
}

// ─── AlertBox — mirrors gfn_ShowMessage() ────────────────────────────────────

function AlertBox({ alert, onClose }: { alert: AlertState; onClose: () => void }) {
  if (!alert.visible) return null;
  const titleMap = { E: "Error", A: "Warning", I: "Information" };
  const titleBg = alert.type === "E" ? "#cc0000" : "#2f6089";
  return (
    <>
      <div style={{ position: "fixed", inset: 0, backgroundColor: "#cccccc", opacity: 0.6, zIndex: 99998 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        width: 506, minHeight: 180, backgroundColor: "white", zIndex: 99999,
        fontFamily: "Tahoma", fontSize: 14, boxShadow: "0 4px 16px rgba(0,0,0,0.3)", overflow: "hidden",
      }}>
        <div style={{
          background: titleBg, color: "white", padding: "6px 10px",
          fontFamily: "Tahoma", fontSize: 13,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>{titleMap[alert.type]}</span>
        </div>
        <div style={{ padding: "20px 24px 10px", color: "#525252", minHeight: 70 }}
          dangerouslySetInnerHTML={{ __html: alert.message.replace(/\n/g, "<br/>") }}
        />
        <div style={{ textAlign: "center", padding: "10px 0 16px" }}>
          <button onClick={onClose} style={{
            backgroundColor: "#119ECC", color: "white", border: "none", borderRadius: 3,
            padding: "6px 28px", fontFamily: "Trebuchet MS", fontSize: "10pt",
            cursor: "pointer", width: 80, height: 28,
          }}>OK</button>
        </div>
      </div>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function EL_TutorAdminRoles() {
  // Read query string — mirrors hidSchoolCode/F/L from Request.QueryString("S/F/L")
  const qp = new URLSearchParams(window.location.search);
  const schoolCode = qp.get("S") ?? "";
  const familyCode = qp.get("F") ?? "";
  const loginParam = qp.get("L") ?? "";

  const [columns, setColumns] = useState<Column[]>([]);
  const [resetMd, setResetMd] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertState>({ visible: false, message: "", type: "A" });

  const [checked, setChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    apiFetchRoles(schoolCode, familyCode, loginParam)
      .then((data) => {
        setColumns(data.columns);
        setResetMd(data.reset_md);
        setCount(data.count);

        // Build initial checked map from columns data
        const initChecked: Record<string, boolean> = {};
        data.columns.forEach((col) => {
          col.groups.forEach((g) => {
            if (g.type === "role" && g.id !== undefined) {
              initChecked[g.id] = g.checked ?? false;
            }
          });
        });
        setChecked(initChecked);

        // mirrors fn_Show() — show error if present
        if (data.error) {
          setAlert({ visible: true, message: data.error, type: "E" });
        }
      })
      .catch((e) => setAlert({ visible: true, message: e.message, type: "E" }))
      .finally(() => setLoading(false));
  }, [schoolCode, familyCode, loginParam]);

  function buildPermissionsString(): string {
    let result = "";
    for (let i = 1; i <= count; i++) {
      const id = String(i);
      const el = document.getElementById(`chk${id}`) as HTMLInputElement | null;
      if (el !== null) {
        result += el.checked ? "Y" : "N";
      } else {
        result += "N";
      }
    }
    return result;
  }

  const handleSave = useCallback(() => {
    const permissions = buildPermissionsString();
    setSaving(true);
    apiSaveRoles({ school_code: schoolCode, family_code: familyCode, login: loginParam, permissions, reset_md: resetMd })
      .then((data) => {
        setColumns(data.columns);
        setResetMd(data.reset_md);
        setCount(data.count);

        const updatedChecked: Record<string, boolean> = {};
        data.columns.forEach((col) => {
          col.groups.forEach((g) => {
            if (g.type === "role" && g.id !== undefined) {
              updatedChecked[g.id] = g.checked ?? false;
            }
          });
        });
        setChecked(updatedChecked);

        if (data.error) {
          setAlert({ visible: true, message: data.error, type: "E" });
        } else if (data.status_msg) {
          setAlert({
            visible: true,
            message: data.status_msg || "Roles saved successfully.",
            type: "I"
          });
        }
      })
      .catch((e) => setAlert({ visible: true, message: e.message, type: "E" }))
      .finally(() => setSaving(false));
  }, [schoolCode, familyCode, loginParam, resetMd, count]);

  const handleBack = useCallback(() => {
    window.location.href =
      `EL_ListTAUsers?S=${encodeURIComponent(schoolCode)}&F=${encodeURIComponent(familyCode)}&L=${encodeURIComponent(loginParam)}`;
  }, [schoolCode, familyCode, loginParam]);

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: Arial, sans-serif; }

        /* mirrors: <table border='1' cellpadding='5' cellspacing='0' width='100%'> */
        .roles-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #969696;
        }
        .roles-table td {
          vertical-align: top;
          padding: 0px;
          background-color: #f2f2f2;
          font-size: 19px;
        }

        /* mirrors: background-color:teal; color:white; font-weight:bold;
                    text-align:center; padding:3px 0px; font-size:16px */
        .role-main-header {
          background-color: teal;
          color: white;
          font-weight: bold;
          text-align: center;
          padding: 3px 0px;
          font-size: 16px;
        }

        /* mirrors: font-weight:bold; color:blue; padding-top:4px */
        .role-sub-header {
          font-weight: bold;
          color: blue;
          padding-top: 4px;
          text-decoration: underline;
          text-align: left;
        }

        /* role row */
        .role-item {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 1px 0;
        }

        /* mirrors: <asp:Button Width="100px" Height="30px" /> */
        .btn-action {
          width: 100px;
          height: 30px;
          cursor: pointer;
          font-family: Arial, sans-serif;
          font-size: 10pt;
        }
      `}</style>

      <div style={{ textAlign: "center", fontFamily: "Arial", fontSize: 14, padding: 4 }}>
        {loading ? (
          <Loading />
        ) : (

          <table className="roles-table">
            <tbody>
              <tr>
                {columns.map((col) => (
                  <td key={col.col} valign="top">
                    <table style={{ border: 0, cellPadding: 2, cellSpacing: 0, width: "100%" } as React.CSSProperties}>
                      <tbody>
                        {col.groups.map((group, gi) => {
                          if (group.type === "header") {
                            return (
                              <tr key={gi}>
                                <td valign="top">
                                  {/* mirrors: <div style='background-color:teal;color:white;...'> */}
                                  <div className="role-main-header">{group.label}</div>
                                </td>
                              </tr>
                            );
                          }
                          if (group.type === "subheader") {
                            return (
                              <tr key={gi}>
                                {/* mirrors: <td style='font-weight:bold;color:blue;padding-top:4px;'><u>label</u></td> */}
                                <td valign="top" className="role-sub-header">
                                  <u>{group.label}</u>
                                </td>
                              </tr>
                            );
                          }
                          // type === "role"
                          const id = group.id!;
                          const isChecked = checked[id] ?? false;
                          return (
                            <tr key={gi}>
                              <td valign="top">
                                <div className="role-item">
                                  {/*
                                   * mirrors:
                                   *   <input type='checkbox' id='chkN' checked onclick='fn_SetColor(N)' />
                                   *   <span id='spnN' style='color:red/black'>label</span>
                                   */}
                                  <input
                                    type="checkbox"
                                    id={`chk${id}`}
                                    checked={isChecked}
                                    onChange={(e) => {
                                      // mirrors fn_SetColor(pID) — updates colour via state
                                      setChecked((prev) => ({ ...prev, [id]: e.target.checked }));
                                    }}
                                  />
                                  <span
                                    id={`spn${id}`}
                                    style={{ color: isChecked ? "red" : "black" }}
                                  >
                                    {group.label}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* mirrors: T762 — <asp:CheckBox ID="chkResetMD" Text="RESET Maths Doctor" /> */}
      <div style={{ textAlign: "center", padding: 10, width: "58%" }}>
        <label style={{ fontFamily: "Arial", fontSize: "large" }}>
          <input
            type="checkbox"
            checked={resetMd}
            onChange={(e) => setResetMd(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          RESET Maths Doctor
        </label>
      </div>

      {/* mirrors: <div align="center" style="padding:10px;">
                    btnSubmit (Save) &nbsp; btnBack (Back)
                  </div> */}
      <div style={{ textAlign: "center", padding: 10 }}>
        <br />
        {/* mirrors: btnSubmit OnClientClick="fn_Roles()" */}
        <button className="btn-action" onClick={handleSave} disabled={saving}>
          {saving && <Loading />}
          <span style={{ marginLeft: saving ? "8px" : "0" }}>
            {saving ? "Saving..." : "Save"}
          </span>
        </button>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        {/* mirrors: btnBack */}
        <button className="btn-action" onClick={handleBack}>
          Back
        </button>
      </div>

      {/* mirrors: gfn_ShowMessage() in fn_Show() */}
      {alert.visible && (
  <CustomAlert
    message={alert.message}
    onClose={() =>
      setAlert((a) => ({
        ...a,
        visible: false
      }))
    }
  />
)}
    </>
  );
}